import React, { useState, useMemo } from 'react';
// import { collection, query, where, getDocs } from 'firebase/firestore';
// import { db } from '../services/firebase';
import api from '../services/api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    Clock, CheckCircle, XCircle, Loader2,
    Search, TrendingUp, UserCheck
} from 'lucide-react';

const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

// Recebe 'clients' para filtrar os ignorados
const RelatoriosClientesScreen = ({ currentUser, clients = [] }) => {
    const getLocalYYYYMMDD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(getLocalYYYYMMDD(firstDay));
    const [endDate, setEndDate] = useState(getLocalYYYYMMDD(today));
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchReportData = async () => {
        if (!currentUser?.companyId) return;
        setLoading(true);
        try {
            const response = await api.get('/reports/clients', {
                params: { startDate, endDate }
            });
            setReportData(response.data || []);
        } catch (error) {
            console.error("Erro relatório clientes:", error);
            // alert("Erro ao buscar dados."); 
        } finally {
            setLoading(false);
        }
    };

    const metrics = useMemo(() => {
        // Backend returns aggregated data now
        // reportData is already an array of clients

        // Filter empty names if needed
        let clientsArray = reportData.map(c => {
            const dbClient = (clients || []).find(cl => cl._id === c.id || cl.id === c.id);
            return {
                ...c,
                // Ensure compatibility
                tmeSeconds: c.tmeSeconds || 0,
                tmaSeconds: c.tmaSeconds || 0,
                dbName: dbClient?.name || c.name || 'Desconhecido',
                dbPhone: dbClient?.phone ? dbClient.phone.replace(/\\D/g, '') : '',
                dbCompany: dbClient?.company || ''
            };
        });

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            clientsArray = clientsArray.filter(c =>
                (c.dbName && c.dbName.toLowerCase().includes(lower)) ||
                (c.dbPhone && c.dbPhone.toLowerCase().includes(lower)) ||
                (c.dbCompany && c.dbCompany.toLowerCase().includes(lower)) ||
                (c.id && c.id.toLowerCase().includes(lower))
            );
        }

        clientsArray.sort((a, b) => b.total - a.total);

        const topClientsData = clientsArray.slice(0, 5).map(c => ({
            name: c.dbName ? c.dbName.split(' ')[0] : 'Unknown',
            fullName: c.dbName || 'Unknown',
            total: c.total
        }));

        return {
            totalClients: clientsArray.length,
            clientsArray,
            topClientsData,
            // Calculate global averages from pre-calculated per-client data is an approximation if weights aren't used
            // but strict sum/count is better if available. 
            // Since backend gives per-client averages, we can sum specific seconds if we had total durations.
            // For now, let's just average the averages (simple) or sum total if available.
        };
    }, [reportData, searchTerm]);

    const filteredClients = useMemo(() => {
        if (!searchTerm) return metrics.clientsArray;
        const lower = searchTerm.toLowerCase();
        return metrics.clientsArray.filter(c =>
            (c.dbName && c.dbName.toLowerCase().includes(lower)) ||
            (c.dbPhone && c.dbPhone.toLowerCase().includes(lower)) ||
            (c.dbCompany && c.dbCompany.toLowerCase().includes(lower)) ||
            (c.id && c.id.toLowerCase().includes(lower))
        );
    }, [metrics.clientsArray, searchTerm]);

    return (
        <div className="p-8 h-full overflow-y-auto bg-gray-50">
            <div className="flex justify-end mb-6">
                <div className="flex flex-wrap items-end gap-2 bg-white p-2 rounded-lg shadow-sm border">
                    <div><label className="block text-xs text-gray-500 ml-1">Início</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm" /></div>
                    <div><label className="block text-xs text-gray-500 ml-1">Fim</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm" /></div>
                    <button onClick={fetchReportData} disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Filtrar"}</button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between"><div><p className="text-sm font-medium text-gray-500">Clientes Únicos Atendidos</p><h3 className="text-3xl font-bold text-gray-800 mt-1">{metrics.totalClients}</h3></div><div className="p-3 bg-blue-50 text-blue-600 rounded-lg"><UserCheck className="w-6 h-6" /></div></div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between"><div><p className="text-sm font-medium text-gray-500">Top Cliente (Volume)</p><h3 className="text-lg font-bold text-gray-800 mt-1 truncate w-48">{metrics.clientsArray[0]?.dbName || '---'}</h3><p className="text-xs text-gray-400">{metrics.clientsArray[0]?.total || 0} atendimentos</p></div><div className="p-3 bg-green-50 text-green-600 rounded-lg"><TrendingUp className="w-6 h-6" /></div></div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between"><div><p className="text-sm font-medium text-gray-500">Média de Espera (Global)</p><h3 className="text-3xl font-bold text-gray-800 mt-1">{metrics.totalClients > 0 ? formatDuration(metrics.clientsArray.reduce((acc, c) => acc + c.tmeSeconds, 0) / metrics.totalClients) : '00:00:00'}</h3></div><div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg"><Clock className="w-6 h-6" /></div></div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-6">Top 5 Clientes por Volume</h3>
                <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={metrics.topClientsData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" hide />
                            <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                            <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                            <Bar dataKey="total" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} label={{ position: 'right', fill: '#6b7280', fontSize: 12 }} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h3 className="text-lg font-bold text-gray-800">Detalhamento por Cliente</h3>
                    <div className="relative w-full md:w-64"><input type="text" placeholder="Buscar cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" /><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /></div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-medium border-b">
                            <tr><th className="px-6 py-4">Cliente / Telefone</th><th className="px-6 py-4 text-center">Total</th><th className="px-6 py-4 text-center">Resolvidos</th><th className="px-6 py-4 text-center">Cancelados</th><th className="px-6 py-4 text-center">T.M.E (Espera)</th><th className="px-6 py-4 text-center">T.M.A (Duração)</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredClients.length > 0 ? (filteredClients.map((client) => (
                                <tr key={client.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <p className="font-bold text-gray-900 leading-tight">{client.dbName}</p>
                                        <p className="text-xs text-blue-600 font-medium mb-0.5">{client.dbPhone || client.id || 'Sem número'}</p>
                                        {client.dbCompany && <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider break-words max-w-[200px]">🏢 {client.dbCompany}</p>}
                                    </td>
                                    <td className="px-6 py-4 text-center font-bold">{client.total}</td>
                                    <td className="px-6 py-4 text-center text-green-600 flex items-center justify-center gap-1">{client.ended} <CheckCircle className="w-3 h-3" /></td>
                                    <td className="px-6 py-4 text-center text-red-600">{client.canceled > 0 ? <span className="flex items-center justify-center gap-1">{client.canceled} <XCircle className="w-3 h-3" /></span> : '-'}</td>
                                    <td className="px-6 py-4 text-center text-gray-600">{client.tme}</td>
                                    <td className="px-6 py-4 text-center text-gray-600">{client.tma}</td>
                                </tr>
                            ))) : (<tr><td colSpan="6" className="px-6 py-8 text-center text-gray-400">Nenhum cliente encontrado para o período.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RelatoriosClientesScreen;